import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const CorporateServices = () => {
  return (
    <div className="min-h-screen bg-slate-900 relative overflow-hidden">
      {/* Organic gradient on the right with animations */}
      <div className="absolute -top-20 -right-20 w-[800px] h-[800px]">
        <div 
          className="absolute top-20 right-0 w-[600px] h-[600px] bg-gradient-to-br from-purple-500/30 via-indigo-500/25 to-blue-500/20 rounded-full blur-3xl"
          style={{
            animation: 'floatSlow 10s ease-in-out infinite'
          }}
        ></div>
        <div 
          className="absolute top-60 right-40 w-[500px] h-[500px] bg-gradient-to-tl from-indigo-400/25 via-purple-400/20 to-violet-400/15 rounded-full blur-2xl"
          style={{
            animation: 'driftRight 12s ease-in-out infinite'
          }}
        ></div>
      </div>

      {/* Organic gradient on the bottom left with animations */}
      <div className="absolute -bottom-40 -left-40 w-[900px] h-[900px]">
        <div 
          className="absolute bottom-0 left-0 w-[700px] h-[700px] bg-gradient-to-br from-indigo-500/35 via-purple-500/25 to-violet-500/20 rounded-full blur-3xl"
          style={{
            animation: 'floatLeft 14s ease-in-out infinite'
          }}
        ></div>
        <div 
          className="absolute bottom-40 left-40 w-[600px] h-[600px] bg-gradient-to-tl from-purple-400/30 via-indigo-400/20 to-blue-400/15 rounded-full blur-2xl"
          style={{
            animation: 'driftLeft 8s ease-in-out infinite'
          }}
        ></div>
      </div>

      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-32 px-4 sm:px-6 lg:px-8 bg-slate-900 relative overflow-hidden min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto w-full">
          <div className="text-left">
            <div className="mb-8">
              <span className="text-white text-sm font-semibold tracking-wider uppercase opacity-90">FOCUS AREAS</span>
            </div>
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-light text-white leading-tight">
              Corporate Services
            </h1>
            <p className="text-xl text-gray-300 mt-8 max-w-3xl font-light leading-relaxed">
              Comprehensive corporate support services designed to streamline your business operations and ensure full regulatory compliance.
            </p>
          </div>
        </div>
      </section>

      {/* Services Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left column - Header */}
            <div>
              <h2 className="text-3xl md:text-5xl font-bold text-white font-AfacadFluxLight leading-tight mb-8">
                Streamlining corporate operations with expert support
              </h2>
            </div>
            {/* Right column - Content */}
            <div className="space-y-6">
              <p className="text-lg text-gray-300 leading-relaxed">
                Our comprehensive corporate services are designed to handle the complex regulatory landscape while you focus on growing your business. We provide <span className="text-purple-300 font-medium">expert liaison with government organizations</span>, ensuring smooth communication and compliance across all regulatory requirements.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                From <span className="text-purple-300 font-medium">employee funds management</span> to comprehensive <span className="text-purple-300 font-medium">regulatory compliance support</span>, we handle the administrative complexities so your team can concentrate on strategic growth and innovation.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                With our deep understanding of Sri Lankan regulatory frameworks and government processes, we ensure your business stays compliant, efficient, and focused on what matters most.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8 text-left">
              <div className="bg-slate-800/30 p-8 rounded-2xl border border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300">
                <div className="text-purple-300 mb-4">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              <h3 className="text-xl font-light text-white mb-3">Liaison with Government Organizations</h3>
              <p className="text-gray-300 font-light">We act as a trusted bridge between your business and key government institutions, ensuring smooth communication, compliance, and timely submissions.</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-300">
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Facilitate documentation and reporting
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Ensure timely approvals and compliance
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Resolve queries efficiently
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Minimize administrative burdens
                </li>
              </ul>
              </div>
              <div className="bg-slate-800/30 p-8 rounded-2xl border border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300">
                <div className="text-purple-300 mb-4">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              <h3 className="text-xl font-light text-white mb-3">Employee Funds Management (EPF & ETF)</h3>
              <p className="text-gray-300 font-light">We manage Employees' Provident Fund (EPF) and Employees' Trust Fund (ETF) obligations with accuracy and confidentiality.</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-300">
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Accurate monthly contributions
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Timely reports and compliance tracking
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Support for employers and employees
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Reduce risk of delays or errors
                </li>
              </ul>
              </div>
              <div className="bg-slate-800/30 p-8 rounded-2xl border border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300">
                <div className="text-purple-300 mb-4">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
              <h3 className="text-xl font-light text-white mb-3">Regulatory Compliance Support</h3>
              <p className="text-gray-300 font-light">We assist businesses in fulfilling statutory requirements with institutions such as IRD, CBSL, and ROC.</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-300">
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Compliance with IRD, Central Bank, and ROC
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Company registration and annul returns
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Tax compliance and financial reporting
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Reduce risks and ensure adherence
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Services Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <div className="mb-4">
              <span className="text-sm font-semibold tracking-widest uppercase text-purple-300">COMPREHENSIVE CORPORATE SUPPORT</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-AfacadFluxLight font-light text-white leading-tight max-w-4xl">
              Expert corporate services tailored to your business needs
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div className="bg-slate-800/20 p-8 rounded-2xl border border-slate-700/30">
                <h3 className="text-2xl font-light text-white mb-4">Government Liaison Services</h3>
                <p className="text-gray-300 font-light mb-6">We act as your trusted intermediary with government institutions, handling all documentation, reporting, and compliance requirements efficiently.</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Documentation and reporting facilitation
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Timely approvals and compliance management
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Query resolution and administrative support
                  </li>
                </ul>
              </div>
              
              <div className="bg-slate-800/20 p-8 rounded-2xl border border-slate-700/30">
                <h3 className="text-2xl font-light text-white mb-4">Employee Funds Management</h3>
                <p className="text-gray-300 font-light mb-6">Comprehensive EPF and ETF management ensuring accurate contributions, timely reporting, and full compliance with regulatory requirements.</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Monthly contribution processing
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Compliance tracking and reporting
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Employer and employee support
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="bg-slate-800/20 p-8 rounded-2xl border border-slate-700/30">
                <h3 className="text-2xl font-light text-white mb-4">Regulatory Compliance</h3>
                <p className="text-gray-300 font-light mb-6">Expert guidance on statutory requirements with IRD, CBSL, and ROC, ensuring your business stays compliant and risk-free.</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Tax compliance and reporting
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Company registration and annual returns
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Financial reporting and statutory obligations
                  </li>
                </ul>
              </div>
              
              <div className="bg-slate-800/20 p-8 rounded-2xl border border-slate-700/30">
                <h3 className="text-2xl font-light text-white mb-4">Risk Management & Advisory</h3>
                <p className="text-gray-300 font-light mb-6">Proactive risk assessment and mitigation strategies to protect your business from regulatory and compliance risks.</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Compliance risk assessment
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Regulatory change monitoring
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Strategic compliance planning
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-white leading-tight max-w-4xl font-AfacadFluxLight">
              How it Works
            </h2>
          </div>
          
          <div className="relative">
            <Swiper
              modules={[Navigation, Pagination]}
              spaceBetween={30}
              slidesPerView={1}
              centeredSlides={true}
              navigation={{
                nextEl: '.swiper-button-next-custom-process',
                prevEl: '.swiper-button-prev-custom-process'
              }}
              pagination={{
                clickable: true,
                el: '.swiper-pagination-custom-process'
              }}
              breakpoints={{
                640: { slidesPerView: 1 },
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 }
              }}
              className="h-auto"
            >
              <SwiperSlide>
                <div className="bg-slate-800/30 rounded-3xl p-8 h-full border border-slate-700/50 backdrop-blur-sm">
                  <div className="mb-6">
                    <span className="text-sm font-semibold tracking-widest uppercase text-gray-300">1. ASSESS</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-light text-purple-300 mb-6 leading-tight">
                    Understand Your Corporate Needs
                  </h3>
                  <p className="text-lg text-gray-300 leading-relaxed mb-8">
                    We conduct a comprehensive assessment of your current corporate structure, compliance requirements, and administrative processes to identify areas for improvement.
                  </p>
                  <div className="flex justify-center">
                    <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center border border-white/10">
                      <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="bg-slate-800/30 rounded-3xl p-8 h-full border border-slate-700/50 backdrop-blur-sm">
                  <div className="mb-6">
                    <span className="text-sm font-semibold tracking-widest uppercase text-gray-300">2. IMPLEMENT</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-light text-purple-300 mb-6 leading-tight">
                    Deploy Comprehensive Solutions
                  </h3>
                  <p className="text-lg text-gray-300 leading-relaxed mb-8">
                    We implement tailored corporate services including government liaison, employee funds management, and regulatory compliance support to streamline your operations.
                  </p>
                  <div className="flex justify-center">
                    <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center border border-white/10">
                      <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="bg-slate-800/30 rounded-3xl p-8 h-full border border-slate-700/50 backdrop-blur-sm">
                  <div className="mb-6">
                    <span className="text-sm font-semibold tracking-widest uppercase text-gray-300">3. MAINTAIN</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-light text-purple-300 mb-6 leading-tight">
                    Ongoing Support & Optimization
                  </h3>
                  <p className="text-lg text-gray-300 leading-relaxed mb-8">
                    We provide continuous monitoring, compliance management, and optimization of your corporate processes to ensure long-term success and regulatory adherence.
                  </p>
                  <div className="flex justify-center">
                    <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center border border-white/10">
                      <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>

            {/* Custom Navigation Buttons */}
            <div className="swiper-button-prev-custom-process absolute left-0 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              </div>
            <div className="swiper-button-next-custom-process absolute right-0 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>

            {/* Custom Pagination */}
            <div className="swiper-pagination-custom-process mt-8 flex justify-center"></div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      {/* <section className="py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <div className="mb-4">
              <span className="text-sm font-semibold tracking-widest uppercase text-purple-300">INDUSTRIES WE SERVE</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-AfacadFluxLight font-light text-white leading-tight max-w-4xl">
              Corporate expertise across diverse business sectors
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Technology</h3>
              <p className="text-gray-300 text-sm">SaaS, fintech, and emerging tech</p>
            </div>
            
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Financial Services</h3>
              <p className="text-gray-300 text-sm">Investment firms and banks</p>
            </div>
            
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Real Estate</h3>
              <p className="text-gray-300 text-sm">Property development and investment</p>
            </div>
            
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-600 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Professional Services</h3>
              <p className="text-gray-300 text-sm">Consulting and advisory firms</p>
            </div>
            
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Manufacturing</h3>
              <p className="text-gray-300 text-sm">Production and industrial</p>
            </div>
            
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Healthcare</h3>
              <p className="text-gray-300 text-sm">Medical and health tech</p>
            </div>
          </div>
        </div>
      </section> */}

      {/* Benefits Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <div className="mb-4">
              <span className="text-sm font-semibold tracking-widest uppercase text-purple-300">WHY CHOOSE US</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-AfacadFluxLight font-light text-white leading-tight max-w-4xl">
              Proven expertise in building sustainable corporate foundations
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-16 lg:gap-20">
            <div className="space-y-6">
              <div className="text-purple-300">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-2xl font-light text-white">Global Expertise</h3>
                <p className="text-lg text-gray-300 leading-relaxed font-light">International experience across multiple jurisdictions and regulatory environments.</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="text-purple-300">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-2xl font-light text-white">Proven Track Record</h3>
                <p className="text-lg text-gray-300 leading-relaxed font-light">Successfully helped hundreds of companies establish and optimize their corporate structures.</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="text-purple-300">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-2xl font-light text-white">Ongoing Support</h3>
                <p className="text-lg text-gray-300 leading-relaxed font-light">Continuous guidance and support as your business grows and evolves.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-AfacadFluxLight font-light text-white leading-tight mb-8">
            Ready to Transform Your Corporate Operations?
          </h2>
          <p className="text-xl text-gray-300 mb-12 font-light">
            Let our expert team help you build a stronger, more efficient corporate foundation
          </p>
          <div className="flex justify-center">
            <Link to="/contact" className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-10 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl font-AfacadFluxLight uppercase tracking-wide">
              Get Started Today
            </Link>
          </div>
        </div>
      </section>


      {/* CSS Animations */}
      <style jsx>{`
        @keyframes floatSlow {
          0%, 100% {
            transform: translate(0px, 0px) rotate(0deg);
          }
          33% {
            transform: translate(80px, -100px) rotate(3deg);
          }
          66% {
            transform: translate(-60px, 60px) rotate(-2deg);
          }
        }

        @keyframes driftRight {
          0%, 100% {
            transform: translate(0px, 0px) scale(1);
          }
          25% {
            transform: translate(40px, -60px) scale(1.1);
          }
          50% {
            transform: translate(-32px, 32px) scale(0.88);
          }
          75% {
            transform: translate(48px, 40px) scale(1.06);
          }
        }

        @keyframes floatLeft {
          0%, 100% {
            transform: translate(0px, 0px) rotate(0deg);
          }
          33% {
            transform: translate(-72px, 88px) rotate(-3deg);
          }
          66% {
            transform: translate(100px, -72px) rotate(2.5deg);
          }
        }

        @keyframes driftLeft {
          0%, 100% {
            transform: translate(0px, 0px) scale(1);
          }
          25% {
            transform: translate(-48px, 64px) scale(1.12);
          }
          50% {
            transform: translate(32px, -40px) scale(0.84);
          }
          75% {
            transform: translate(-56px, -32px) scale(1.08);
          }
        }
      `}</style>
    </div>
  );
};

export default CorporateServices; 